'use client';

import { API_KEY_ERROR_CODES, isTheBoatDbApiError } from '@interfaces/apiKey';
import styles from '@styles/ApiKeys/ApiKeys.module.scss';
import { Button, Result } from 'antd';

/**
 * The failure states this screen can land in, told apart by the API's error
 * code. A 429 from the *management* limiter is the one worth spelling out: it
 * is not the partner rate limit, and the fix is to wait rather than to change
 * anything.
 */

type Props = {
  error: unknown;
  onRetry: () => void;
  isRetrying: boolean;
};

type ErrorCopy = {
  title: string;
  subTitle: string;
  status: 'error' | 'warning' | 'info' | '403' | '500';
  canRetry: boolean;
};

function describeRetryAfter(seconds: number | null): string {
  if (seconds === null) {
    return 'Wait a moment and try again.';
  }

  if (seconds < 60) {
    return `Try again in ${seconds} second${seconds === 1 ? '' : 's'}.`;
  }

  const minutes = Math.ceil(seconds / 60);

  return `Try again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`;
}

function describe(error: unknown): ErrorCopy {
  if (!isTheBoatDbApiError(error)) {
    return {
      status: 'error',
      title: 'We could not load your API keys',
      subTitle: 'Check your connection and try again.',
      canRetry: true,
    };
  }

  switch (error.code) {
    case API_KEY_ERROR_CODES.RATE_LIMITED:
      return {
        status: 'warning',
        title: 'Too many requests to key management',
        subTitle: `This limit protects the key management endpoints and is separate from your API key's rate limit — your live integrations are unaffected. ${describeRetryAfter(
          error.retryAfterSeconds,
        )}`,
        canRetry: true,
      };
    case API_KEY_ERROR_CODES.UNAUTHORIZED:
      return {
        status: '403',
        title: 'Your session has expired',
        subTitle: 'Sign in again to manage your API keys.',
        canRetry: false,
      };
    case API_KEY_ERROR_CODES.SERVICE_UNAVAILABLE:
      return {
        status: 'warning',
        title: 'Key management is temporarily unavailable',
        subTitle:
          'We could not reach the key service. Your existing keys keep working — only creating and revoking is affected.',
        canRetry: true,
      };
    default:
      return {
        status: 'error',
        title: 'We could not load your API keys',
        subTitle: 'Something went wrong on our side. Try again, and contact support if it keeps happening.',
        canRetry: true,
      };
  }
}

const ApiKeysErrorState: React.FC<Props> = ({ error, onRetry, isRetrying }) => {
  const { title, subTitle, status, canRetry } = describe(error);
  const requestId = isTheBoatDbApiError(error) ? error.requestId : null;

  return (
    <div className={styles.panel}>
      <Result
        status={status}
        title={title}
        subTitle={subTitle}
        extra={
          canRetry ? (
            <Button color="primary" variant="solid" loading={isRetrying} onClick={onRetry}>
              Try again
            </Button>
          ) : undefined
        }
      >
        {requestId !== null && (
          <p className={styles.footnote}>
            Quote request ID <code>{requestId}</code> if you contact support.
          </p>
        )}
      </Result>
    </div>
  );
};

export { ApiKeysErrorState };
